import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SwPush, SwUpdate, VersionEvent } from '@angular/service-worker';
import { interval, Subject, takeUntil, tap } from 'rxjs';
import { RouterOutlet } from '@angular/router';
import { NzConfigService } from 'ng-zorro-antd/core/config';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { ThemeService } from '../services/theme.service';
import { AppUpdateService } from './shared/services';
import { LoadingMarkComponent } from './shared/components/loading-mark/loading-mark.component';
import { environment } from 'src/environments/environment';

@Component({
  standalone: true,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [CommonModule, RouterOutlet, LoadingMarkComponent, NzButtonModule, NzIconModule]
})
export class AppComponent implements OnInit {
  private static readonly MAX_INSTALL_RETRIES = 3;
  private destroy$ = new Subject<void>();
  private userActivityTimeout!: any;
  private installFailureCount = 0;

  @ViewChild('dnSpinIndicator', { static: true }) dnSpinIndicator!: TemplateRef<void>;

  // App-wide update banner — moved here from LayoutComponent (DisplayNow standup, 2026-08-05) so
  // it appears whether or not the user is logged in. Update detection below already ran app-wide
  // (AppComponent is the root component for every route); only the banner's rendering was scoped
  // to the authenticated shell.
  updateAvailable$ = this.appUpdateService.updateAvailable$;

  constructor(
    private swUpdate: SwUpdate,
    private themeService: ThemeService,
    private appUpdateService: AppUpdateService,
    private nzConfigService: NzConfigService,
  ) {}

  ngOnInit(): void {
    this.themeService.init();
    this.initializeServiceWorker();

    // Swaps the default ng-zorro spin dots for the branded loading mark everywhere.
    // Flip environment.customLoadingIndicatorEnabled to revert to the default instantly.
    if (environment.customLoadingIndicatorEnabled) {
      this.nzConfigService.set('spin', { nzIndicator: this.dnSpinIndicator });
    }
  }

  private initializeServiceWorker(): void {
    if (this.swUpdate.isEnabled) {
      console.log('Service Worker is enabled. Setting up update checks...');
      
      // Check for updates immediately on app load
      this.checkForUpdates();
      
      // Perform initial update check
      this.swUpdate.checkForUpdate()
        .then(updateAvailable => {
          if (updateAvailable) {
            console.log('Update check: New version available');
          } else {
            console.log('Update check: App is up to date');
          }
        })
        .catch(error => {
          console.warn('Update check failed:', error);
        });
      
      // Check for updates periodically (every 2 minutes)
      interval(2 * 60 * 1000) 
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          console.log('Performing scheduled update check...');
          this.swUpdate.checkForUpdate().catch(error => {
            console.warn('Scheduled update check failed:', error);
          });
        });
    } else {
      console.log('Service Worker is not enabled');
    }
  }
  private checkForUpdates(): void {
    this.swUpdate.versionUpdates
      .pipe(
        tap((event: VersionEvent) => {
          switch (event.type) {
            case 'VERSION_DETECTED':
              console.log('New version detected. Installing...');
              break;
            case 'VERSION_READY':
              console.log('New version ready. Reloading to get new version...');
              this.installFailureCount = 0;
              this.handleNewVersionReady();
              break;
            case 'VERSION_INSTALLATION_FAILED':
              this.installFailureCount++;
              if (this.installFailureCount <= AppComponent.MAX_INSTALL_RETRIES) {
                console.warn(
                  `Failed to install the update. Retrying (${this.installFailureCount}/${AppComponent.MAX_INSTALL_RETRIES})...`
                );
                setTimeout(() => this.swUpdate.checkForUpdate().catch(console.error), 30000);
              } else {
                // A stale cached asset is preventing the service worker from installing the
                // new version, so retrying checkForUpdate() will keep failing. Stop the retry
                // loop and surface the update banner so the user can recover with a refresh.
                console.warn(
                  'Failed to install the update after multiple attempts. Prompting a refresh to recover.'
                );
                this.handleNewVersionReady();
              }
              break;
            default:
              console.log('Service worker version update event:', event.type);
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private handleNewVersionReady(): void {
    // A new release is ready. Instead of a blocking modal, surface a
    // dismissible banner (rendered app-wide, right here in AppComponent) so
    // the user can refresh whenever they choose, without losing unsaved work.
    this.appUpdateService.updateAvailable$.next(true);
  }

  dismissUpdateBanner(): void {
    this.appUpdateService.dismiss();
  }

  applyUpdate(): void {
    this.appUpdateService.applyUpdate();
  }

  ngOnDestroy(): void {
    clearTimeout(this.userActivityTimeout);
    this.destroy$.next();
    this.destroy$.complete();
  }
  
}
